import React, { useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Rating,
  SxProps,
  Theme,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import StarIcon from "@mui/icons-material/Star";

interface RatingsProps {
  userId: number;
  ratings?: {
    avg_overall?: number | string;
    avg_sales?: number | string;
    avg_expertise?: number | string;
    avg_professionalism?: number | string;
    avg_engagement?: number | string;
  };
  compact?: boolean;
  sx?: SxProps<Theme>;
}

const Ratings: React.FC<RatingsProps> = ({ ratings, compact = false, sx }) => {
  const theme = useTheme();

  useEffect(() => {}, [ratings]);

  if (!ratings) {
    return (
      <Card>
        <CardContent>
          <Typography>No ratings available</Typography>
        </CardContent>
      </Card>
    );
  }

  const ratingData = Array.isArray(ratings) ? ratings[0] : ratings;

  const ratingCategories = [
    { key: "avg_overall", label: "Overall" },
    { key: "avg_sales", label: "Sales" },
    { key: "avg_expertise", label: "Expertise" },
    { key: "avg_professionalism", label: "Prof." },
    { key: "avg_engagement", label: "Engage." },
  ];

  const parseRating = (value: number | string | undefined): number => {
    if (typeof value === "number") return value;
    if (typeof value === "string") return parseFloat(value) || 0;
    return 0;
  };

  if (compact) {
    return (
      <Box
        display="flex"
        gap={2}
        flexWrap="wrap"
        sx={{
          p: 1,
          borderRadius: 1,
          backgroundColor: "rgba(0, 0, 0, 0.02)",
          boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
          ...sx,
        }}
      >
        {ratingCategories.map(({ key, label }) => {
          const ratingValue = ratingData[key as keyof typeof ratingData];
          const parsedRating = parseRating(ratingValue);

          return (
            <Box
              key={key}
              display="flex"
              alignItems="center"
              sx={{
                minWidth: "100px",
                backgroundColor: theme.palette.background.paper,
                borderRadius: 1,
                px: 1,
                py: 0.5,
                boxShadow: "0 1px 2px rgba(0, 0, 0, 0.03)",
                transition: "all 0.2s ease",
                "&:hover": {
                  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.06)",
                  transform: "translateY(-1px)",
                },
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  fontSize: "0.75rem",
                  width: "45px",
                }}
              >
                {label}
              </Typography>
              <StarIcon
                sx={{
                  color: theme.palette.primary.main,
                  fontSize: "1rem",
                  ml: 0.5,
                  mr: 0.5,
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: "bold",
                  color: theme.palette.primary.main,
                }}
              >
                {parsedRating.toFixed(1)}
              </Typography>
            </Box>
          );
        })}
      </Box>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography
          variant="h6"
          gutterBottom
          color={theme.palette.primary.main}
        >
          Ratings
        </Typography>
        {ratingCategories.map(({ key, label }) => {
          const ratingValue = ratingData[key as keyof typeof ratingData];
          const parsedRating = parseRating(ratingValue);

          return (
            <Box key={key} display="flex" alignItems="center" mb={1}>
              <Typography component="legend" minWidth={120}>
                {label}:
              </Typography>
              <Rating
                name={key}
                value={parsedRating}
                precision={0.1}
                readOnly
                sx={{
                  color: theme.palette.primary.main,
                  "& .MuiRating-iconFilled": {
                    color: theme.palette.primary.main,
                  },
                  "& .MuiRating-iconHover": {
                    color: theme.palette.primary.dark,
                  },
                }}
              />
              <Typography ml={1}>{parsedRating.toFixed(1)}</Typography>
            </Box>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default Ratings;
